import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  List, ListItem, ListItemText, TextField, Tooltip, Typography
} from '@mui/material';
import { DeleteOutline, Send } from '@mui/icons-material';
import api from '../api';

export interface Versand { versendet_am: string; empfaenger: number; tage?: number[] | null }
interface Empfaenger { id: number; email: string; name: string | null }

export const versandText = (v: Versand) => {
  const d = new Date(v.versendet_am);
  const datum = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  const zeit = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `Gesendet am ${datum} um ${zeit} an ${v.empfaenger} Empfänger`;
};

const fehlerText = (e: any, standard: string) => e?.response?.data?.fehler || standard;

// tage = Beschriftungen der fertigen, noch nicht versendeten Tage (nur diese werden gesendet)
export default function VersandDialog({ jahr, kw, versand, tage, verteilerAendern = true, onClose, onGesendet }: {
  jahr: number; kw: number; versand: Versand | null | undefined; tage: string[]; verteilerAendern?: boolean;
  onClose: () => void; onGesendet: (v: Versand, antwort: any) => void;
}) {
  const [liste, setListe] = useState<Empfaenger[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [arbeitet, setArbeitet] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api.get('/wochenplan/verteiler', { signal: controller.signal })
      .then(res => setListe(res.data))
      .catch(e => { if (!controller.signal.aborted) setFehler(fehlerText(e, 'Der Verteiler konnte nicht geladen werden.')); });
    return () => controller.abort();
  }, []);

  const hinzufuegen = async (e: React.FormEvent) => {
    e.preventDefault();
    setFehler('');
    setArbeitet(true);
    try {
      const res = await api.post('/wochenplan/verteiler', { email, name });
      setListe(l => [...(l || []), res.data]);
      setEmail('');
      setName('');
    } catch (err) {
      setFehler(fehlerText(err, 'Die Adresse konnte nicht gespeichert werden.'));
    } finally { setArbeitet(false); }
  };

  const entfernen = async (id: number) => {
    setFehler('');
    try {
      await api.delete(`/wochenplan/verteiler/${id}`);
      setListe(l => (l || []).filter(x => x.id !== id));
    } catch (err) {
      setFehler(fehlerText(err, 'Die Adresse konnte nicht entfernt werden.'));
    }
  };

  const senden = async () => {
    setFehler('');
    setArbeitet(true);
    try {
      const res = await api.post(`/wochenplan/woche/${jahr}/${kw}/senden`, {}, { timeout: 60000 });
      onGesendet(res.data.versand, res.data);
    } catch (err) {
      setFehler(fehlerText(err, 'Der Wochenplan konnte nicht gesendet werden.'));
      setArbeitet(false);
    }
  };

  return (
    <Dialog open onClose={arbeitet ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Wochenplan KW {kw} freigeben & senden</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Die fertigen Tage gehen als Übersicht per E-Mail an alle Adressen im Verteiler und gelten danach als versendet.
        </Typography>
        {tage.length
          ? <Alert severity="success" sx={{ mb: 2 }}>Gesendet werden: {tage.join(', ')}</Alert>
          : <Alert severity="warning" sx={{ mb: 2 }}>Es ist noch kein Tag fertig. Bitte zuerst im Plan Tage als „fertig“ markieren.</Alert>}
        {versand && <Alert severity="info" sx={{ mb: 2 }}>Zuletzt: {versandText(versand)}.</Alert>}
        {fehler && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFehler('')}>{fehler}</Alert>}

        <Typography variant="subtitle2">Verteiler</Typography>
        {liste === null && !fehler ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box> : (
          <List dense>
            {(liste || []).map(v => (
              <ListItem key={v.id} secondaryAction={verteilerAendern &&
                <Tooltip title="Entfernen"><IconButton edge="end" aria-label={`${v.email} entfernen`} disabled={arbeitet} onClick={() => entfernen(v.id)}><DeleteOutline /></IconButton></Tooltip>
              }>
                <ListItemText primary={v.name || v.email} secondary={v.name ? v.email : undefined} />
              </ListItem>
            ))}
            {liste?.length === 0 && <ListItem><ListItemText secondary="Noch keine Empfänger eingetragen." /></ListItem>}
          </List>
        )}
        {!verteilerAendern && <Typography variant="caption" sx={{ color: 'text.secondary' }}>Den Verteiler kann nur ein Admin ändern.</Typography>}
        {verteilerAendern && <Box component="form" onSubmit={hinzufuegen} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" label="E-Mail" type="email" required value={email} onChange={e => setEmail(e.target.value)}
            inputProps={{ maxLength: 120 }} sx={{ flex: 2, minWidth: 200 }} />
          <TextField size="small" label="Name (optional)" value={name} onChange={e => setName(e.target.value)}
            inputProps={{ maxLength: 80 }} sx={{ flex: 1, minWidth: 140 }} />
          <Button type="submit" disabled={arbeitet || !email.trim()}>Hinzufügen</Button>
        </Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={arbeitet}>Schließen</Button>
        <Button variant="contained" startIcon={arbeitet ? <CircularProgress size={16} color="inherit" /> : <Send />}
          onClick={senden} disabled={arbeitet || !liste?.length || !tage.length}>
          {tage.length ? `${tage.length === 1 ? '1 Tag' : `${tage.length} Tage`} senden` : 'Jetzt senden'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
